/**
 * The words a customer reads for each qualification question (spec
 * 2026-09-26, section 6). Shared so the customer's qualification dialog and
 * the admin console's Customer answers tab say the same thing: an operator
 * reads each answer next to the question exactly as the customer saw it.
 */
import type { QuestionId } from "./qualification";

export interface QuestionCopy {
  label: (taxYear: number) => string;
  help?: string;
}

export const QUESTION_COPY: Record<QuestionId, QuestionCopy> = {
  "eci.us_employees": {
    label: (year) =>
      `In ${year}, did anyone work inside the US for your company?`,
    help: "Employees, contractors or agents who were physically in the US. A registered agent does not count, and freelancers working from abroad do not count.",
  },
  "eci.us_office": {
    label: (year) =>
      `In ${year}, did your company have an office, store or other property in the US?`,
    help: "An office you lease or own, or a shop. Your registered agent’s address and a virtual mailbox do not count.",
  },
  "eci.us_inventory": {
    label: (year) => `In ${year}, did your company keep inventory in the US?`,
    help: "For example Amazon FBA or another US warehouse. Dropshipping from abroad and digital products do not count.",
  },
  "eci.services_in_us": {
    label: (year) =>
      `In ${year}, did you or anyone at your company perform services while in the US?`,
    help: "Work done while physically in the US. Working from your home country for US clients does not count.",
  },
  "eci.us_real_estate": {
    label: (year) =>
      `In ${year}, did your company earn income from US real estate?`,
    help: "Rent from US property or a gain from selling it. Bank interest and stock dividends do not count.",
  },
  "ccorp.dividends_paid": {
    label: (year) =>
      `In ${year}, did your corporation pay dividends to its shareholders?`,
  },
  "ccorp.us_source_payments_to_foreign_owner": {
    label: (year) =>
      `In ${year}, did your corporation pay interest, royalties or rent to its foreign owner?`,
  },
  "ccorp.all_owners_us_persons": {
    label: () =>
      "Is every shareholder a US citizen, or a US resident for tax purposes (green card or living in the US)?",
    help: "Answer No if at least one shareholder lives outside the US and is not a US citizen or green card holder. A US mailing address alone does not make someone a US person.",
  },
  "ccorp.foreign_owner_25": {
    label: (year) =>
      `In ${year}, did one foreign person own at least 25% of your corporation?`,
    help: "Count shares held directly or through another company or a family member, by vote or by value. A foreign person is a company formed outside the US, or someone who is not a US citizen or US resident (green card or living in the US).",
  },
  "smllc.owner_us_person": {
    label: () =>
      "Is the owner a US citizen, or a US resident for tax purposes (green card or living in the US)?",
    help: "We prepare returns only for companies with a foreign owner. A US mailing address or a virtual mailbox does not make an owner a US person.",
  },
  "mmllc.all_partners_individuals": {
    label: () =>
      "Are all of your company’s members individual people (not companies or trusts)?",
  },
  "mmllc.any_us_person_partner": {
    label: () =>
      "Is any member a US citizen, a green card holder, or someone with a Social Security number?",
  },
  // T3: U.S. bank deposit interest is exempt from withholding and not on Form 1042-S
  // (IRC 871(i)(2)(A); Treas. Reg. 1.1441-1(b)(4)(ii), 1.1461-1(c)(2)(ii)(A)), so it is
  // not asked. The key stays `mmllc.us_source_passive_income` for stored answers.
  "mmllc.us_source_passive_income": {
    label: (year) =>
      `In ${year}, did your company receive US dividends, rent or royalties, or interest other than from a US bank account?`,
    help: "Interest on a US checking or savings account doesn't count. A treasury, money-market or investment sweep pays dividends, which do count.",
  },
  "mmllc.member_change_during_year": {
    label: (year) =>
      `In ${year}, did anyone join or leave the LLC, or did any member's share change?`,
    help: "Count a member who joined, left, sold or gave away part of their interest, or whose profit, loss or capital percentage changed during the year. Answer No if the same members held the same percentages all year.",
  },
  "eci.has_itin": {
    label: () =>
      "Do you have an ITIN (Individual Taxpayer Identification Number)?",
    help: "If you do not have one, we prepare Form W-7 with your return so the IRS can issue one.",
  },
  "eci.itin": {
    label: () => "Your ITIN",
    help: "9 digits starting with 9, as on the letter the IRS sent you.",
  },
  "eci.days_in_us": {
    label: () =>
      "How many days were you physically in the US in each of these years?",
    help: "Count every day you were in the US, even part of a day. Enter 0 for a year you were not there.",
  },
  "eci.green_card": {
    label: () => "Do you hold a US green card?",
  },
  "eci.us_wages": {
    label: (year) => `In ${year}, did you receive US wages (a Form W-2)?`,
  },
};
